// =============================================================================
// identity-client.http.ts — Adaptateur HTTP RÉEL de l'IdentityClient (onboarding fournisseur, D4).
// =============================================================================
// Remplace le stub-first (Option B) derrière le MÊME DI seam (IdentityClientProvider) : les
// hooks/écrans consomment l'interface IdentityClient, l'impl est substituée sans toucher l'UI.
// Miroir des routes FOURNISSEUR de supplier-auth.controller.ts (@Controller('supplier/auth')).
//
// AUTORITÉ = API : émission (TTL, plafond anti-abus, DE.MagicLinkIssued), consommation usage-unique
// stricte et ouverture de session sont l'API. Le FRONT ne fabrique/hashe aucun token ; il déclenche
// l'envoi et présente le secret du lien. L'email vérifié saisi en landing EST l'identifiant de
// connexion (`{ email }` sur le fil, EF-IDA-5) — l'API résout le credential par email puis fige le
// siret depuis l'identité, jamais du body.
//
// FLUX consumeMagicLink (2 appels composés) :
//   1. POST /supplier/auth/session { token }  → ouvre la SupplierSession, pose le cookie OPAQUE
//      httpOnly `supplier_session` (+ cookie CSRF `supplier_csrf`). Réponse 200 { connected: true }.
//   2. GET  /supplier/auth/whoami             → { siret } résolu par le TenantInterceptor sur la
//      session tout juste ouverte (cookie transmis via credentials:'include'). → { tenantId: siret }.
//
// SÉCURITÉ / CSRF : magic-link et session sont des routes PUBLIC (@PublicRoute) — elles précèdent la
// session et ne portent donc PAS de CSRF (la route session est justement celle qui POSE le cookie
// CSRF). whoami est un GET (@SupplierRoute) : pas de double-submit sur les lectures. Aucun en-tête
// CSRF n'est donc requis ici. La session est portée par le cookie httpOnly (`credentials:'include'`).
//
// CONTRAT D'ERREUR (résolu, DETTE 1 / S20-REMEDIATION) : côté serveur, MagicLinkExceptionFilter
// mappe désormais chaque échec de consommation (POST /session) en {statut HTTP, code} DISTINCTS,
// avec un corps `{ statusCode, code, message }` : expiré→410/TOKEN_EXPIRED, consommé→409/TOKEN_CONSUMED,
// invalide→401/TOKEN_INVALID. Cet adaptateur lit le `code` du CORPS (autorité serveur) pour peupler
// `IdentityErrorCode`, avec repli sur le status (429→RATE_LIMITED, 401/403→TOKEN_INVALID, autre→UNKNOWN)
// si le `code` est absent/inconnu. Le plafond anti-abus reste absorbé par le 202 constant (émission).

import {
  IdentityApiError,
  type AdminSessionDto,
  type IdentityClient,
  type IdentityErrorCode,
  type MagicLinkConsumeResult,
  type MagicLinkRequestResult,
} from '@/api/identity-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/** Réponse de whoami (miroir de SupplierAuthController.whoami : `{ siret }`). */
interface WhoamiView {
  readonly siret?: unknown;
}

/** Normalise un email (trim + minuscules) pour l'écho d'accusé, en parité avec le stub. */
function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Lit un cookie NON-httpOnly depuis `document.cookie` (mécanisme double-submit CSRF documenté ;
 * PAS du state métier en localStorage → conforme RULE_REF_006). Renvoie '' si absent. Défensif
 * hors navigateur (SSR/test sans DOM) : `document` peut être indéfini → repli sur ''.
 */
function readCookie(name: string): string {
  if (typeof document === 'undefined') {
    return '';
  }
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return '';
}

/** Codes d'erreur métier reconnus (garde le repli UNKNOWN pour tout code serveur inattendu). */
const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<IdentityErrorCode>([
  'TOKEN_EXPIRED',
  'TOKEN_CONSUMED',
  'TOKEN_INVALID',
  'RATE_LIMITED',
  'UNKNOWN',
]);

/**
 * Repli par status HTTP quand le corps ne porte pas de `code` exploitable (autorité = status).
 * Reste défensif : les échecs sans code structuré (500 générique, proxy) collapsent en UNKNOWN.
 */
function codeFromStatus(status: number): IdentityErrorCode {
  if (status === 429) {
    return 'RATE_LIMITED';
  }
  if (status === 401 || status === 403) {
    return 'TOKEN_INVALID';
  }
  return 'UNKNOWN';
}

/**
 * Dérive l'IdentityErrorCode : le `code` du CORPS fait AUTORITÉ (peuplé par MagicLinkExceptionFilter
 * côté serveur), avec repli sur le status si le `code` est absent/inconnu. Ne réinterprète jamais la
 * règle métier : relaie fidèlement la discrimination serveur.
 */
function codeFromError(body: StructuredErrorBody | undefined, status: number): IdentityErrorCode {
  const serverCode = body?.code;
  if (serverCode !== undefined && KNOWN_ERROR_CODES.has(serverCode)) {
    return serverCode as IdentityErrorCode;
  }
  return codeFromStatus(status);
}

/**
 * Adaptateur HTTP réel de l'onboarding fournisseur. `baseUrl` par défaut vide (chemins relatifs :
 * SPA et API partagent l'origine, ou un reverse proxy route `/supplier/*`). En test, fetch est mocké.
 */
export class HttpIdentityClient implements IdentityClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Construit l'URL absolue (baseUrl + chemin relatif racine API). */
  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Exécute une requête same-origin (cookie de session transmis via credentials). Renvoie la
   * Response brute (OK garanti) ; sur erreur HTTP, lève une IdentityApiError (code dérivé du
   * status réel + message serveur relayé tel quel). Les routes ciblées ne portent pas de CSRF.
   */
  private async fetchOk(path: string, init: RequestInit): Promise<Response> {
    const response = await fetch(this.url(path), {
      credentials: 'include',
      ...init,
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return response;
  }

  /** Convertit une réponse non-OK en IdentityApiError typée (code du status, message serveur). */
  private async toApiError(response: Response): Promise<IdentityApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la connexion fournisseur.`;
    return new IdentityApiError(codeFromError(body, response.status), message);
  }

  public async activate(token: string): Promise<void> {
    // ACTIVATION INITIALE (F1/F2) : l'API consomme l'ActivationToken, active le credential ET ouvre
    // la session (pose les cookies via Set-Cookie). Corps réel = `{ token }`. Réponse 200 constante
    // `{ activated: true }` — on ne l'inspecte pas : la session ouverte (cookies) est le seul acquis
    // consommé côté UI. En échec, fetchOk lève l'IdentityApiError typée (401 → TOKEN_INVALID, etc.).
    await this.fetchOk('/supplier/auth/activate', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  }

  public async requestMagicLink(email: string): Promise<MagicLinkRequestResult> {
    const normalized = normalizeEmail(email);
    // L'email vérifié saisi EST l'identifiant de connexion (`{ email }`, EF-IDA-5) — jamais un
    // siret. Réponse 202 CONSTANTE (anti-énumération) : on n'inspecte pas le corps, l'accusé suffit.
    await this.fetchOk('/supplier/auth/magic-link', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: normalized }),
    });
    return { status: 'sent', email: normalized };
  }

  public async consumeMagicLink(token: string): Promise<MagicLinkConsumeResult> {
    // 1. Ouvre la session (cookie httpOnly posé par l'API). Corps réel = `{ token }`.
    await this.fetchOk('/supplier/auth/session', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    // 2. Résout le tenant via la session tout juste ouverte (cookie transmis). Autorité API.
    const whoami = await this.fetchOk('/supplier/auth/whoami', { method: 'GET' });
    const view = (await whoami.json()) as WhoamiView;
    const siret = typeof view.siret === 'string' ? view.siret : '';
    if (siret.length === 0) {
      // whoami sans siret = session non résolue : on ne devine pas un tenant, on échoue clair.
      throw new IdentityApiError(
        'UNKNOWN',
        'Session ouverte mais tenant non résolu. Reconnectez-vous.',
      );
    }
    return { tenantId: siret };
  }

  public async getAdminSession(): Promise<AdminSessionDto> {
    // Sonde de session ADMIN (cookie httpOnly transmis via credentials:'include'). LECTURE → pas de
    // CSRF (route session côté guard, GET). 401/403 (pas de session) ⇒ fetchOk lève l'IdentityApiError
    // typée : le bootstrap retombe alors sur le chemin anonyme/landing. AUTORITÉ = API : `subject` vient
    // de la réponse serveur, jamais fabriqué côté client. On fige `role:'AdminFull'` (mapping explicite).
    const response = await this.fetchOk('/admin/auth/session', { method: 'GET' });
    const view = (await response.json()) as { subject?: unknown; name?: unknown };
    const subject = typeof view.subject === 'string' ? view.subject : '';
    if (subject.length === 0) {
      // Session ouverte mais sujet non résolu : on ne devine pas une identité, on échoue clair.
      throw new IdentityApiError('UNKNOWN', 'Session admin sans sujet résolu. Reconnectez-vous.');
    }
    // DEC-47 — nom d'affichage optionnel (null tant que l'annuaire admin est vide, fallback subject).
    const name =
      typeof view.name === 'string' && view.name.length > 0 ? view.name : null;
    return { subject, role: 'AdminFull', name };
  }

  public async adminLogout(): Promise<void> {
    // Révocation SERVEUR de la session admin (transition d'état) + effacement des cookies, 204.
    // CSRF double-submit : on relaie le cookie NON-httpOnly `admin_csrf` dans l'en-tête `x-csrf-token`
    // (aligné sur le guard back). `credentials:'include'` transmet le cookie de session httpOnly.
    // BEST-EFFORT (pas de fetchOk) : l'autorité de révocation est le serveur ; un 401/403 résiduel
    // est de toute façon une déconnexion effective côté client, qui navigue vers la landing quoi
    // qu'il arrive. On n'inspecte pas l'issue HTTP et on ne jette jamais — on avale toute erreur
    // réseau pour ne pas bloquer la purge de l'état d'auth (navigation pleine page côté appelant).
    try {
      await fetch(this.url('/admin/auth/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': readCookie('admin_csrf') },
      });
    } catch {
      // no-op : déconnexion best-effort, le front redirige de toute façon vers la landing.
    }
  }
}
